/**
 * Content Import Module
 * Public API for importing external content (Google Docs, Word, etc.) into AST format
 */

import { cleanClipboardHtml } from './htmlCleaner'
import { HtmlToAstOptions, parseHtmlToAst } from './htmlToAst'
import { ContentAST, createEmptyAST, createParagraph, createTextNode, generateBlockId } from '../index'

export interface ImportOptions {
  defaultLang?: 'he' | 'en' | 'mixed'
  defaultDir?: 'rtl' | 'ltr'
  preserveClasses?: boolean
}

export interface ImportResult {
  success: boolean
  ast?: ContentAST
  errors: string[]
  warnings?: string[]
}

/**
 * Import HTML content from clipboard (Google Docs, Word, etc.)
 * Handles the full pipeline: messy HTML → clean HTML → AST
 */
export function importHtmlToAst(rawHtml: string, options?: ImportOptions): ImportResult {
  try {
    // Step 1: Clean the messy clipboard HTML
    const cleanHtml = cleanClipboardHtml(rawHtml)

    if (!cleanHtml.trim()) {
      return {
        success: false,
        errors: ['Clipboard HTML resulted in empty content after cleaning'],
      }
    }

    // Step 2: Convert clean HTML to AST
    const parserOptions: HtmlToAstOptions = {
      defaultLang: options?.defaultLang || 'mixed',
      defaultDir: options?.defaultDir || 'ltr',
      preserveClasses: options?.preserveClasses ?? true,
      cleanupWhitespace: true,
    }

    const parseResult = parseHtmlToAst(cleanHtml, parserOptions)

    // Step 3: Return unified result
    return {
      success: parseResult.success,
      ast: parseResult.ast,
      errors: parseResult.errors.map((e) => e.message),
    }
  } catch (error) {
    return {
      success: false,
      errors: [`Failed to parse clipboard HTML: ${error}`],
    }
  }
}

/**
 * Import plain text content from clipboard
 * Creates simple paragraph structure from plain text
 */
export function importTextToAst(text: string, options?: ImportOptions): ImportResult {
  try {
    if (!text.trim()) {
      return {
        success: false,
        errors: ['Clipboard text is empty'],
      }
    }

    // Create base AST structure
    const ast = createEmptyAST(options?.defaultLang || 'mixed', options?.defaultDir || 'ltr')

    // Convert plain text lines to AST blocks
    const lines = text.split(/\r?\n/)
    const blocks = lines
      .filter((line) => line.trim()) // Remove empty lines
      .map((line) => {
        const paragraph = createParagraph()
        paragraph.id = generateBlockId()
        paragraph.content = [createTextNode(line.trim())]
        return paragraph
      })

    // If no content lines, create one empty paragraph
    if (blocks.length === 0) {
      const paragraph = createParagraph()
      paragraph.id = generateBlockId()
      paragraph.content = [createTextNode('')]
      blocks.push(paragraph)
    }

    ast.blocks = blocks

    return {
      success: true,
      ast,
      errors: [],
    }
  } catch (error) {
    return {
      success: false,
      errors: [`Failed to parse clipboard text: ${error}`],
    }
  }
}

